import { useState, useEffect, useCallback, useMemo } from "react";
import { Link, useLoaderData } from "react-router-dom";
import {
  subscriberAPI,
  exportSubscribersCSV,
  type SubscriberResponse,
  type SubscriberStatsResponse,
} from "../../lib/api";
import type { PodcastResponse } from "../../types/api";
import styles from "./SubscribersPage.module.css";

export interface SubscribersPageData {
  podcast: PodcastResponse;
  organizationId: string;
  initialSubscribers: SubscriberResponse[];
  initialTotal: number;
  stats: SubscriberStatsResponse;
}

const PAGE_SIZE = 50;

const STATUS_OPTIONS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "cancelled", label: "Cancelled" },
  { value: "expired", label: "Expired" },
  { value: "revoked", label: "Revoked" },
];

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function relativeDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 30) return `${diffDays}d ago`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`;
  return `${Math.floor(diffDays / 365)}y ago`;
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case "active":
      return styles.badgeActive;
    case "cancelled":
      return styles.badgeCancelled;
    case "expired":
      return styles.badgeExpired;
    case "revoked":
      return styles.badgeRevoked;
    default:
      return "";
  }
}

export default function SubscribersPage() {
  const { podcast, organizationId, initialSubscribers, initialTotal, stats } =
    useLoaderData() as SubscribersPageData;

  const [subscribers, setSubscribers] = useState(initialSubscribers);
  const [total, setTotal] = useState(initialTotal);
  const [statusFilter, setStatusFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscribers = useCallback(
    async (status: string, newOffset: number) => {
      setLoading(true);
      setError(null);
      try {
        const params: { status?: string; limit: number; offset: number } = {
          limit: PAGE_SIZE,
          offset: newOffset,
        };
        if (status) params.status = status;
        const result = await subscriberAPI.list(organizationId, podcast.id, params);
        setSubscribers(result.subscribers);
        setTotal(result.total);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : "Failed to load subscribers");
      } finally {
        setLoading(false);
      }
    },
    [organizationId, podcast.id],
  );

  useEffect(() => {
    fetchSubscribers(statusFilter, offset);
  }, [statusFilter, offset, fetchSubscribers]);

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setStatusFilter(e.target.value);
    setOffset(0);
  };

  const filteredSubscribers = useMemo(() => {
    if (!searchQuery) return subscribers;
    const q = searchQuery.toLowerCase();
    return subscribers.filter((s) => s.email.toLowerCase().includes(q));
  }, [subscribers, searchQuery]);

  const handleExport = () => {
    exportSubscribersCSV(filteredSubscribers);
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.breadcrumb}>
          <Link to={`/organizations/${organizationId}/podcasts`}>Podcasts</Link>
          <span className={styles.sep}>/</span>
          <Link to={`/organizations/${organizationId}/podcasts/${podcast.id}`}>{podcast.name}</Link>
          <span className={styles.sep}>/</span>
          <span>Subscribers</span>
        </div>
        <h1 className={styles.title}>Subscribers</h1>
      </div>

      <div className={styles.statGrid}>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Active</span>
          <span className={styles.statValue}>{stats.totalActive}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>MRR</span>
          <span className={styles.statValue}>{formatCents(stats.mrr)}/mo</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Cancelled</span>
          <span className={styles.statValue}>{stats.totalCancelled}</span>
        </div>
        <div className={styles.statCard}>
          <span className={styles.statLabel}>Expired</span>
          <span className={styles.statValue}>{stats.totalExpired}</span>
        </div>
      </div>

      <div className={styles.toolbar}>
        <select
          className={styles.filterSelect}
          value={statusFilter}
          onChange={handleStatusChange}
          aria-label="Filter by status"
        >
          {STATUS_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <input
          className={styles.searchInput}
          type="search"
          placeholder="Search by email…"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          aria-label="Search subscribers by email"
        />
        <button className={styles.exportBtn} onClick={handleExport} type="button">
          Export CSV
        </button>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      {loading ? (
        <div className={styles.loading}>Loading subscribers…</div>
      ) : filteredSubscribers.length === 0 ? (
        <div className={styles.empty}>No subscribers found.</div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Feed Accesses</th>
                  <th>Subscribed</th>
                  <th>Last Access</th>
                </tr>
              </thead>
              <tbody>
                {filteredSubscribers.map((sub) => (
                  <tr key={sub.id}>
                    <td>{sub.email}</td>
                    <td>
                      <span className={`${styles.badge} ${statusBadgeClass(sub.status)}`}>
                        {sub.status}
                      </span>
                    </td>
                    <td>{sub.feedAccessCount}</td>
                    <td>{relativeDate(sub.createdAt)}</td>
                    <td>{sub.lastFeedAccess ? relativeDate(sub.lastFeedAccess) : "Never"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className={styles.pagination}>
              <button
                className={styles.paginationBtn}
                disabled={offset === 0}
                onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                type="button"
              >
                ← Previous
              </button>
              <span className={styles.paginationInfo}>
                Page {currentPage} of {totalPages}
              </span>
              <button
                className={styles.paginationBtn}
                disabled={offset + PAGE_SIZE >= total}
                onClick={() => setOffset(offset + PAGE_SIZE)}
                type="button"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
